import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { product_item_availability } from 'commons/constants';
import getBreadcrumbsForPage from 'commons/getBreadcrumbsForPage';
import useProductOrderETA from '../useProductOrderETA';
import { findAvailabilityAndVisibility } from '../utils';

/**
 *  Used in the PDP to determine if product is out of stock
 *  or discontinued based on stock response (both EFG internal and FFG).
 *  This controls the notification displayed in the PDP.
 *
 *  Returns object of shape
 *  {
 *    loading,
 *    error
 *    data : {
 *     isOutOfStock,
 *     showNotification,
 *     isEFGInternal,
 *     isDiscontinued
 *    }
 *  }
 */

const OUT_OF_STOCK_ALERT_DEFAULT = {
  show: false,
  productId: null
};
const usePDPGetStockAvailability = t0 => {
  const $ = _c(21);
  const {
    basicDetails,
    productOptions
  } = t0;
  const {
    loading: basicDetailsLoading,
    error: basicDetailsError,
    data: basicDetailsData
  } = basicDetails;
  const productId = basicDetailsData?.id;
  const {
    loading: optionsLoading,
    data: optionsData,
    error: optionsError
  } = productOptions;
  const isEFGProduct = !!basicDetailsData?.efg_flag;
  const isExternalProduct = !!basicDetailsData?.is_external;
  const isEFGInternal = isEFGProduct && !isExternalProduct;
  const isEFGExternal = isEFGProduct && isExternalProduct;
  const isMarkedDiscontinued = !!basicDetailsData?.discontinued;
  basicDetailsData?.attributes;
  let t1;
  bb0: {
    const {
      availability
    } = findAvailabilityAndVisibility(basicDetailsData?.attributes || []);
    if (`${availability}` === `${product_item_availability.out_of_stock}` || `${availability}` === `${product_item_availability.disabled}`) {
      t1 = true;
      break bb0;
    }
    t1 = false;
  }
  const isMarkedOOS = t1;
  const {
    stock: t2
  } = optionsData;
  const stock = t2 === undefined ? null : t2;
  const isStockLoading = !!stock?.loading;
  const isMarkedOOSOrDiscontinued = !!(productId && (isMarkedOOS || isMarkedDiscontinued));
  const isProductOrStockLoading = !basicDetailsData || !stock || isStockLoading || typeof stock?.inStockOptionIds === "undefined" || productId !== stock?.idForStock;
  const inStockOptionIds = stock?.inStockOptionIds;
  const selectedOptionsStockQty = stock?.selectedOptionsStockQty;
  const isOutOfStock = !isProductOrStockLoading && (isEFGExternal && isMarkedOOS || isEFGInternal && !isMarkedOOSOrDiscontinued && !inStockOptionIds?.length && !selectedOptionsStockQty || !inStockOptionIds?.length && !selectedOptionsStockQty && isMarkedOOS);
  const isDiscontinued = !isProductOrStockLoading && (isEFGExternal && isMarkedDiscontinued || !inStockOptionIds?.length && !selectedOptionsStockQty && isMarkedDiscontinued);
  const shouldGetProductOrderETA = !isProductOrStockLoading && (isMarkedOOS || isEFGInternal && !isMarkedOOSOrDiscontinued);
  const params = useParams();
  const categoryName = params?.category;
  let t3;
  bb1: {
    if (!isDiscontinued || !productId) {
      t3 = null;
      break bb1;
    }
    let t4;
    if ($[0] !== categoryName) {
      const allBreadCrumbs = getBreadcrumbsForPage("pdp");
      const productBreadCrumbs = allBreadCrumbs?.[categoryName] && allBreadCrumbs?.[allBreadCrumbs[categoryName]];
      t4 = productBreadCrumbs?.length ? productBreadCrumbs.pop() : null;
      $[0] = categoryName;
      $[1] = t4;
    } else {
      t4 = $[1];
    }
    const finalCategory = t4;
    if (finalCategory) {
      let t5;
      if ($[2] !== finalCategory.label || $[3] !== finalCategory.link || $[4] !== productId) {
        t5 = {
          category: finalCategory.label,
          link: finalCategory.link,
          forId: productId
        };
        $[2] = finalCategory.label;
        $[3] = finalCategory.link;
        $[4] = productId;
        $[5] = t5;
      } else {
        t5 = $[5];
      }
      const updatedDiscontinuedMsg = t5;
      t3 = updatedDiscontinuedMsg;
      break bb1;
    }
    t3 = null;
  }
  const discontinuedCategoryMsg = t3;
  const {
    loading: etaLoading,
    eta
  } = useProductOrderETA(shouldGetProductOrderETA ? basicDetailsData.id : null);
  let t4;
  bb2: {
    if (isOutOfStock) {
      const t5 = eta && !etaLoading ? eta : undefined;
      let t6;
      if ($[6] !== productId || $[7] !== t5) {
        t6 = {
          eta: t5,
          productId,
          show: true
        };
        $[6] = productId;
        $[7] = t5;
        $[8] = t6;
      } else {
        t6 = $[8];
      }
      t4 = t6;
      break bb2;
    }
    t4 = OUT_OF_STOCK_ALERT_DEFAULT;
  }
  const outOfStockAlert = t4;
  const t5 = basicDetailsLoading || optionsLoading;
  const t6 = basicDetailsError || optionsError;
  let t7;
  if ($[9] !== discontinuedCategoryMsg || $[10] !== isDiscontinued || $[11] !== isEFGInternal || $[12] !== isMarkedOOSOrDiscontinued || $[13] !== isOutOfStock || $[14] !== isStockLoading || $[15] !== outOfStockAlert) {
    t7 = {
      discontinuedCategoryMsg,
      isDiscontinued,
      isEFGInternal,
      isMarkedOOSOrDiscontinued,
      isOutOfStock,
      isStockLoading,
      showNotification: outOfStockAlert
    };
    $[9] = discontinuedCategoryMsg;
    $[10] = isDiscontinued;
    $[11] = isEFGInternal;
    $[12] = isMarkedOOSOrDiscontinued;
    $[13] = isOutOfStock;
    $[14] = isStockLoading;
    $[15] = outOfStockAlert;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = {
      loading: t5,
      error: t6,
      data: t7
    };
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
export default usePDPGetStockAvailability;